import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import { Link as RouterLink, useParams } from 'react-router'

import { getService } from '../../api/services'
import type { ServiceDetail } from '../../api/services'
import { can } from '../../auth/session'
import { usePortal } from '../../auth/sessionContext'
import { CodeBlock } from '../../components/ui/CodeBlock'
import { DataState } from '../../components/DataState'
import { Money } from '../../components/Money'
import { UNSET, formatCount } from '../../format'
import { domainForService } from '../../productDomains'
import { ContractTable } from './ContractTable'
import { isMatchContract } from './contract'
import { SANDBOX_LABEL } from './labels'
import { pythonExample } from './sdk'

/**
 * Everything a developer needs before writing the first call.
 *
 * Deliberately one page: the contract, the price, which of their applications
 * may call it, a copyable example, and the way to try it for real. Splitting
 * those across tabs means somebody integrates against the field list without
 * ever seeing that a `matched: false` is an answer rather than a fault.
 */
export function ServiceDetailPage() {
  const { code = '' } = useParams()
  const { membership } = usePortal()
  const customerId = membership.customer.id

  const query = useQuery({
    queryKey: ['client', 'service', customerId, code],
    queryFn: ({ signal }) => getService(customerId, code, signal),
  })

  return (
    <Box>
      <Button component={RouterLink} to="/services" size="small" sx={{ mb: 1 }}>
        بازگشت به دسترسی سرویس‌ها
      </Button>

      <DataState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
      >
        {query.data ? (
          <ServiceBody service={query.data} maySandbox={can(membership, 'run_sandbox')} />
        ) : null}
      </DataState>
    </Box>
  )
}

function ServiceBody({ service, maySandbox }: { service: ServiceDetail; maySandbox: boolean }) {
  const domain = domainForService(service.domain, service.code)
  const matching = isMatchContract(service.contract.result)

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: { sm: 'flex-start' }, mb: 2 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h1" gutterBottom>
            {service.display_name_fa || service.name}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Chip size="small" label={domain.labelFa} />
            <Chip
              size="small"
              variant="outlined"
              label={service.code}
              sx={{ fontFamily: 'monospace' }}
            />
          </Stack>
        </Box>
        {maySandbox ? (
          <Button
            component={RouterLink}
            to={`/services/${service.code}/sandbox`}
            variant="contained"
            sx={{ flexShrink: 0 }}
          >
            {SANDBOX_LABEL.action}
          </Button>
        ) : null}
      </Stack>

      {service.description ? (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
          {service.description}
        </Typography>
      ) : null}

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={3}
          divider={<Divider flexItem orientation="vertical" />}
        >
          <Box>
            <Typography variant="caption" color="text.secondary" component="p">
              بهای هر فراخوانی
            </Typography>
            <Money amountIrr={service.sale_price_irr} whenUnknown={UNSET.salePrice} />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" component="p">
              دستهٔ سرویس
            </Typography>
            <Typography variant="body2">{domain.labelFa}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" component="p">
              تعداد فیلدهای ورودی
            </Typography>
            <Typography variant="body2">{formatCount(service.contract.request.length)}</Typography>
          </Box>
        </Stack>
      </Paper>

      {matching ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>پاسخ «تطبیق ندارد» یک پاسخ موفق است</AlertTitle>
          این سرویس به یک پرسش بله یا خیر پاسخ می‌دهد. اگر مقدار خروجی «تطبیق ندارد» باشد، یعنی
          بررسی انجام شده و پاسخ آن منفی است. این یک نتیجهٔ درست و موفق است، نه خطا؛ بهای فراخوانی
          هم برداشت می‌شود، چون کار انجام شده است. آن را دوباره فراخوانی نکنید؛ پاسخ همان خواهد بود
          و دوباره هزینه می‌گیرد.
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h2" gutterBottom>
          ورودی‌های درخواست
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          مقادیری که هنگام فراخوانی این سرویس می‌فرستید.
        </Typography>
        <ContractTable fields={service.contract.request} showRequired caption="ورودی‌های درخواست" />
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h2" gutterBottom>
          فیلدهای پاسخ
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          مقادیری که در پاسخ موفق دریافت می‌کنید. فیلدهای تودرتو با تورفتگی نمایش داده شده‌اند.
        </Typography>
        <ContractTable
          fields={service.contract.result}
          showRequired={false}
          caption="فیلدهای پاسخ"
        />
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h2" gutterBottom>
          اپلیکیشن‌های دارای دسترسی
        </Typography>
        {service.applications.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            هیچ اپلیکیشنی در حال حاضر به این سرویس دسترسی ندارد.
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {service.applications.map((application) => (
              <Chip
                key={application.id}
                label={application.name}
                component={RouterLink}
                to={`/applications/${application.id}`}
                clickable
              />
            ))}
          </Stack>
        )}
      </Paper>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h2" gutterBottom>
          نمونهٔ فراخوانی با کتابخانهٔ Python
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          شناسه و رمز نمونه‌اند و کار نمی‌کنند؛ اعتبارنامهٔ خودتان را از بخش اعتبارنامه‌ها بسازید.
          راهنمای کامل نصب و خطاها در <RouterLink to="/docs">راهنمای فنی</RouterLink> آمده است.
        </Typography>
        <CodeBlock
          title="نمونهٔ کد"
          caption="مقادیر ورودی ساختگی‌اند."
          code={pythonExample(service.code, service.contract.request, service.contract.result)}
        />
      </Box>
    </Box>
  )
}
